import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { UserDto, UserListItemDto, UserStatus } from '../../config/userConfig'
import useUsers from '../../hooks/useUsers'
import BaseCombobox from '../Combobox/BaseCombobox'
import { BaseComboboxProps } from './BaseCombobox'
import { useTranslation } from 'react-i18next'

type Props = Partial<Pick<BaseComboboxProps<UserListItemDto>, 'filter' | 'multiselect' | 'required' | 'disabled'>> &
    Required<Pick<BaseComboboxProps<UserListItemDto>, 'selectedOptions' | 'onOptionSelect'>>

type UserGroupOption = UserListItemDto & {
    children: UserListItemDto[]
}

export default function UserCombobox(props: Props): ReactElement {
    const { loaded, users } = useUsers()
    const { t: translate } = useTranslation()

    const groupedUsers = useMemo<UserGroupOption[]>(() => {
        const result: UserGroupOption[] = []
        if (loaded) {
            const groups: Map<UserStatus, UserGroupOption> = users
                .filter(entity => props.filter?.(entity) ?? entity.status === UserStatus.Enabled)
                .reduce((map: Map<UserStatus, UserGroupOption>, user: UserDto) => {
                    const group: UserGroupOption = map.get(user.status) || { ...user, children: [] }
                    group.children = [...group.children, user].sort((a, b) => a.fullName.localeCompare(b.fullName))
                    map.set(user.status, group)
                    return map
                }, new Map<UserStatus, UserGroupOption>())

            if (groups.size > 1) {
                groups.forEach(user =>
                    result.push({ ...user, fullName: translate(`user.status.${user.status.toLowerCase()}`) }),
                )
            }
        }
        return result
    }, [loaded, users])

    if (groupedUsers.length > 0) {
        return (
            <BaseCombobox<UserListItemDto, UserGroupOption>
                entities={groupedUsers}
                alphabetical
                isGroup={group => group['children'] !== undefined}
                getGroupEntities={group => group.children}
                getGroupLabel={group => group.fullName}
                getGroupValue={group => group.status}
                getEntityLabel={user => user.fullName}
                getEntityValue={user => user.id}
                placeholderKey={'base.userpicker.placeholder'}
                {...props}
            />
        )
    }

    return (
        <BaseCombobox<UserListItemDto>
            entities={users}
            filter={entity => entity.status === UserStatus.Enabled}
            alphabetical
            getEntityLabel={user => user.fullName}
            getEntityValue={user => user.id}
            placeholderKey={'base.userpicker.placeholder'}
            {...props}
        />
    )
}
